<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { formatDistanceToNow } from 'date-fns';
import IntegrationsAPI from 'dashboard/api/integrations';
import Integration from '../Integration.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Every email provider (Gmail today; Microsoft 365/SES/Resend/SendGrid/
// custom SMTP later) is registered the same way any other Chatwoot
// integration is — via config/integration/apps.yml + the existing
// Integrations::Hook system. This page filters that same generic list down
// to the email-tagged entries (category: 'email') and renders each with
// the existing Integration.vue connect/disconnect widget, so it never
// needs to know Gmail-specific details — only the generic
// `connection_type` capability flag (oauth vs the settings-form flow
// Cal.com already uses) decides which connect affordance to show.
const store = useStore();
const { t } = useI18n();
const integrationGetter = useMapGetter('integrations/getAppIntegrations');

const integrations = computed(() =>
  integrationGetter.value.filter(
    integration => integration.category === 'email'
  )
);

const isAuthorizing = ref(null);
const isTesting = ref(null);
const isSendingTest = ref(null);

// integration.provider_status comes from Integrations::Mail::Base.status_for
// (see api/v1/models/_app.json.jbuilder) — computed server-side from the
// provider's own configured? check plus the account's Hook, so this page
// never has to know which provider it's looking at. 'connection_expired' is
// the one status the backend doesn't proactively compute (it would mean
// polling every provider's API on every page load) — it's set here,
// client-side, only right after a live Test Connection actually fails.
// Tailwind's JIT scanner needs full, literal class strings — not ones
// assembled at runtime from a color token — so each status carries its
// complete `text-n-*-11` class here rather than an interpolated fragment.
const STATUS_META = {
  configuration_required: {
    icon: 'i-lucide-alert-triangle',
    textClass: 'text-n-amber-11',
  },
  ready: { icon: 'i-lucide-circle', textClass: 'text-n-slate-11' },
  connected: {
    icon: 'i-lucide-check-circle-2',
    textClass: 'text-n-teal-11',
  },
  disconnected: { icon: 'i-lucide-x-circle', textClass: 'text-n-ruby-11' },
  connection_expired: {
    icon: 'i-lucide-alert-triangle',
    textClass: 'text-n-amber-11',
  },
};

const expiredIds = ref(new Set());

const statusFor = integration => {
  if (
    integration.provider_status === 'connected' &&
    expiredIds.value.has(integration.id)
  ) {
    return 'connection_expired';
  }
  return integration.provider_status;
};

const statusMeta = integration =>
  STATUS_META[statusFor(integration)] || STATUS_META.ready;

// Reads only whatever the backend chose to expose for this provider via
// apps.yml's `visible_properties` (see config/integration/apps.yml) — never
// assumes Gmail-specific keys, so a future provider that exposes a
// differently-shaped settings hash still renders correctly here.
const connectedSettings = integration => integration.hooks?.[0]?.settings || {};

// `expires_on` is a Rails-serialized timestamp string (see
// OauthCallbackController#handle_mail_integration_response /
// Google::RefreshOauthTokenService), not a Unix timestamp — parsed via
// the native Date constructor rather than dynamicTime's fromUnixTime.
const tokenValidity = expiresOn => {
  const parsed = new Date(expiresOn);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDistanceToNow(parsed, { addSuffix: true });
};

const handleConnect = async integration => {
  if (statusFor(integration) !== 'ready') return;

  isAuthorizing.value = integration.id;
  try {
    const response = await IntegrationsAPI.authorizeEmailProvider(
      integration.id
    );
    window.location.href = response.data.url;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('EMAIL_INTEGRATIONS.CONNECT_ERROR')
    );
  } finally {
    isAuthorizing.value = null;
  }
};

const handleTestConnection = async integration => {
  isTesting.value = integration.id;
  try {
    const response = await IntegrationsAPI.testEmailConnection(integration.id);
    if (response.data.success) {
      expiredIds.value.delete(integration.id);
      useAlert(t('EMAIL_INTEGRATIONS.TEST_CONNECTION_SUCCESS'));
    } else {
      expiredIds.value.add(integration.id);
      useAlert(t('EMAIL_INTEGRATIONS.TEST_CONNECTION_FAILURE'));
    }
  } catch (error) {
    expiredIds.value.add(integration.id);
    useAlert(t('EMAIL_INTEGRATIONS.TEST_CONNECTION_FAILURE'));
  } finally {
    isTesting.value = null;
  }
};

const handleSendTestEmail = async integration => {
  isSendingTest.value = integration.id;
  try {
    await IntegrationsAPI.sendTestEmail(integration.id);
    useAlert(t('EMAIL_INTEGRATIONS.SEND_TEST_SUCCESS'));
  } catch (error) {
    useAlert(t('EMAIL_INTEGRATIONS.SEND_TEST_FAILURE'));
  } finally {
    isSendingTest.value = null;
  }
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="mb-0 text-lg font-medium text-n-slate-12">
      {{ t('EMAIL_INTEGRATIONS.TITLE') }}
    </h2>
    <p class="mt-0 text-sm text-n-slate-11">
      {{ t('EMAIL_INTEGRATIONS.DESCRIPTION') }}
    </p>
    <div
      v-for="integration in integrations"
      :key="integration.id"
      class="flex flex-col w-full gap-2"
    >
      <Integration
        :integration-id="integration.id"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="
          integration.enabled ? 'disconnect' : integration.action
        "
        icon="i-lucide-mail"
      >
        <template
          v-if="!integration.enabled && integration.connection_type === 'oauth'"
          #action
        >
          <Button
            faded
            blue
            :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            :is-loading="isAuthorizing === integration.id"
            :disabled="statusFor(integration) === 'configuration_required'"
            @click="handleConnect(integration)"
          />
        </template>
        <template
          v-else-if="!integration.enabled && integration.action"
          #action
        >
          <router-link
            :to="{
              name: 'settings_applications_integration',
              params: { integration_id: integration.id },
            }"
          >
            <Button
              faded
              blue
              :label="t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            />
          </router-link>
        </template>
        <template v-else-if="!integration.enabled" #action>
          <Label
            :label="t('EMAIL_INTEGRATIONS.COMING_SOON')"
            color="amber"
            compact
          />
        </template>
      </Integration>
      <div
        v-if="!integration.enabled"
        class="flex items-center gap-1.5 px-4 text-sm"
        :class="statusMeta(integration).textClass"
      >
        <span :class="statusMeta(integration).icon" class="text-base" />
        {{
          t(`EMAIL_INTEGRATIONS.STATUS.${statusFor(integration).toUpperCase()}`)
        }}
      </div>
      <div
        v-if="statusFor(integration) === 'configuration_required'"
        class="flex flex-col gap-2 mx-4 p-3 rounded-lg bg-n-amber-2 outline outline-1 outline-n-amber-6 text-sm"
      >
        <p class="m-0 text-n-slate-11 whitespace-pre-line">
          {{
            integration.configuration_instructions ||
            t('EMAIL_INTEGRATIONS.CONFIG_REQUIRED.GENERIC_MESSAGE')
          }}
        </p>
      </div>
      <div v-if="integration.enabled" class="flex flex-col gap-3 px-4 pb-1">
        <div
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1 outline outline-1 outline-n-container text-sm"
        >
          <div
            class="flex items-center gap-1.5 font-medium"
            :class="statusMeta(integration).textClass"
          >
            <span :class="statusMeta(integration).icon" class="text-base" />
            {{
              t(
                `EMAIL_INTEGRATIONS.STATUS.${statusFor(integration).toUpperCase()}`
              )
            }}
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-1 text-n-slate-11">
            <span>
              {{ t('EMAIL_INTEGRATIONS.CONNECTED_DETAILS.PROVIDER') }}:
              <span class="text-n-slate-12">{{ integration.name }}</span>
            </span>
            <span v-if="connectedSettings(integration).email">
              {{ t('EMAIL_INTEGRATIONS.CONNECTED_DETAILS.ACCOUNT') }}:
              <span class="text-n-slate-12">{{
                connectedSettings(integration).email
              }}</span>
            </span>
            <span
              v-if="tokenValidity(connectedSettings(integration).expires_on)"
            >
              {{ t('EMAIL_INTEGRATIONS.CONNECTED_DETAILS.TOKEN_REFRESH') }}:
              <span class="text-n-slate-12">{{
                tokenValidity(connectedSettings(integration).expires_on)
              }}</span>
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            faded
            slate
            size="sm"
            :label="t('EMAIL_INTEGRATIONS.TEST_CONNECTION')"
            :is-loading="isTesting === integration.id"
            @click="handleTestConnection(integration)"
          />
          <Button
            faded
            slate
            size="sm"
            :label="t('EMAIL_INTEGRATIONS.SEND_TEST_EMAIL')"
            :is-loading="isSendingTest === integration.id"
            @click="handleSendTestEmail(integration)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
